import { useEffect, useState } from "react";
import { api, type HomeRequirements, type Level, type SetupStatus } from "../api";
import { errorText } from "../hooks";
import { Help } from "./Help";

export const LEVEL_LABEL: Record<Level, string> = { mandatory: "Mandatory", recommended: "Recommended", off: "Not required" };

const SETUP_ROWS: Array<[keyof HomeRequirements["setup"], string]> = [
  ["chrome", "Chrome, and letting it see the screens"],
  ["monitor_helper", "Identify the monitor (Check my monitor download, reads the EDID)"],
  ["display", "Display settings and the first lux reading (baseline)"],
  ["card_size", "Size check with a bank card"],
  ["practice", "The cord, the keypad and a practice"],
];
const SESSION_ROWS: Array<[string, string]> = [
  ["screen", "Screen check: Chrome, graphics, HDR off, native resolution, steady refresh"],
  ["monitor.identity", "Monitor identity: this screen is a monitor Check my monitor read"],
  ["monitor.model", "Monitor identity: its EDID model is the approved model's"],
  ["monitor.serial", "Monitor identity: it is the monitor sent to this patient (serial)"],
  ["monitor.panel_bits", "Monitor identity: it declares 10-bit input"],
  ["monitor.link_bits", "Monitor identity: the computer sends it 10 bits (macOS)"],
  ["brightness", "Brightness: one lux-meter reading per visit"],
  ["distance", "Distance: the patient confirms it with the cord"],
  ["card_size", "Size check with a bank card, once per visit"],
];
const STATUS: Record<string, string> = {
  done: "done", skipped: "skipped", todo: "to do", not_required: "not needed", before_setup: "set up before the setup existed",
};

function getLevel(req: HomeRequirements, path: string): Level {
  const [a, b] = path.split(".");
  const s = req.session as unknown as Record<string, any>;
  return (b ? s[a!][b] : s[a!]) as Level;
}

function setLevel(req: HomeRequirements, part: "setup" | "session", path: string, v: Level): HomeRequirements {
  const next = structuredClone(req);
  const [a, b] = path.split(".");
  const obj = next[part] as unknown as Record<string, any>;
  if (b) obj[a!][b] = v; else obj[a!] = v;
  return next;
}

function LevelSelect({ value, disabled, label, onChange }: {
  value: Level; disabled: boolean; label: string; onChange: (v: Level) => void;
}) {
  return (
    <select aria-label={label} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as Level)}
      className={`level-${value}`}>
      {(["mandatory", "recommended", "off"] as Level[]).map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
    </select>
  );
}

/** Staff: what this patient's first-time setup and every home session require, and how
 *  far the setup has got. */
export function HomeRequirementsCard({ patientId, canEdit }: { patientId: number; canEdit: boolean }) {
  const [st, setSt] = useState<SetupStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.homeRequirements(patientId).then(setSt, (e) => setError(errorText(e)));
  }, [patientId]);
  const run = async (f: () => Promise<SetupStatus>) => {
    setBusy(true);
    setError(null);
    try {
      setSt(await f());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!st) return error ? <div className="card error">{error}</div> : null;
  const req = st.requirements;
  const own = st.own_requirements;
  const save = (next: HomeRequirements | null) => void run(() => api.setHomeRequirements(patientId, next));
  const step = (k: string) => st.steps.find((x) => x.key === k);
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Home requirements<Help k="req.overview" /></h2>
      <p className="small">
        {own ? "This patient's own." : "From the patient's program."}{" "}
        {canEdit && (own
          ? <button className="link" disabled={busy} onClick={() => save(null)}>Use the program's again</button>
          : <button className="link" disabled={busy} onClick={() => save(req)}>Change them for this patient</button>)}
      </p>
      <h3>First-time setup<Help k="req.setup" /></h3>
      <p className="small muted" style={{ marginTop: 0 }}>At the patient's first sign-in, and again whenever they open
        it or you ask for it. Mandatory steps must be done before the first test; recommended ones may be skipped.</p>
      <table className="req-table">
        <thead><tr><th>step</th><th>requirement</th><th>progress</th></tr></thead>
        <tbody>
          {SETUP_ROWS.map(([k, label]) => {
            const s = step(k);
            return (
              <tr key={k}>
                <td>{label}<Help k={`req.setup.${k}`} /></td>
                <td><LevelSelect value={req.setup[k]} disabled={!own || !canEdit || busy} label={`Setup: ${label}`}
                  onChange={(v) => save(setLevel(req, "setup", k, v))} /></td>
                <td className="small">{s ? STATUS[s.status] : ""}{s?.at ? `, ${new Date(s.at).toLocaleDateString()}` : ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {st.completed_at && <p className="small">Setup finished {new Date(st.completed_at).toLocaleString()}.</p>}
      {canEdit && (
        <p><button className="secondary" disabled={busy} onClick={() => void run(() => api.resetSetup(patientId))}>
          Run the setup again at the next sign-in</button>
          {st.reset_at && <span className="small muted"> Last asked {new Date(st.reset_at).toLocaleDateString()}.</span>}</p>
      )}
      <h3>Every session<Help k="req.session" /></h3>
      <p className="small muted" style={{ marginTop: 0 }}>Once per visit (programs done in one sitting share them).
        Mandatory: must pass. Recommended: the patient sees how to fix it and may go on; the results carry a note.
        Not required: not asked (the screen check still runs and is recorded). A desktop with WebGL2 and a known screen
        size are always needed, and so is another (unapproved) monitor's identity, which gives its size.</p>
      <table className="req-table">
        <thead><tr><th>check</th><th>requirement</th></tr></thead>
        <tbody>
          {SESSION_ROWS.map(([k, label]) => (
            <tr key={k}>
              <td>{label}<Help k={`req.session.${k}`} />
                {k === "monitor.serial" && !st.serial_set && <span className="small muted"> (no serial set on this patient)</span>}</td>
              <td><LevelSelect value={getLevel(req, k)} disabled={!own || !canEdit || busy} label={`Session: ${label}`}
                onChange={(v) => save(setLevel(req, "session", k, v))} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
